import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, BACKEND_URL } from "@/lib/api";

const SERVICES = [
    {
        id: "verified_field_shoot",
        slug: "verified_field_shoot",
        purchasable: true,
        icon: "fa-solid fa-camera-retro",
        title: "Verified Field Shoot",
        price: "Fixed-scope assignment",
        price_detail: "$1,500 deposit · balance on delivery",
        target: "Best for NGOs needing campaign or annual report content",
        description: "A 3-day assignment with an agreed number of images, full consent documentation, and C2PA metadata on every file. Scope and price are fixed before Trenton leaves for the field — no surprises on invoice.",
    },
    {
        id: "ethical_archive_license",
        purchasable: false,
        icon: "fa-solid fa-folder-open",
        title: "Ethical Archive License",
        price: "Tiered licensing",
        price_detail: "From $75 per image",
        target: "Best for publishers needing verified source material",
        description: "License from a searchable archive where every image ships with consent paperwork and C2PA provenance attached. Instant download, clear editorial and campaign tiers, no legal gaps on sourcing.",
        link: "/archive",
    },
    {
        id: "retainer_framework",
        purchasable: false,
        icon: "fa-solid fa-handshake-angle",
        title: "Retainer / Framework Agreement",
        price: "Quarterly content allotment",
        price_detail: "From $350/mo · see subscriptions",
        target: "Ideal for ongoing comms and fundraising needs",
        description: "A predictable quarterly delivery of field imagery for NGOs — same price every quarter, same quality every quarter. Built to slot into framework agreements, grant cycles, and year-end fundraising calendars.",
        link: "#subscribe",
    },
    {
        id: "provenance_audit",
        slug: "provenance_audit",
        purchasable: true,
        icon: "fa-solid fa-magnifying-glass-chart",
        title: "Provenance Audit",
        price: "Low-cost entry offer",
        price_detail: "$450 flat",
        target: "A written report with recommendations",
        description: "A review of your existing image library for consent and authenticity gaps. You get a short written report flagging the images that could expose you legally — and clear next steps to fix them.",
    },
];

const AUDIENCE_COPY = {
    ngo: {
        eyebrow: "For international NGOs & humanitarian comms",
        headline_pre: "Dignified, verified",
        headline_accent: "field storytelling.",
        description:
            "Field imagery your donors, safeguarding team, and legal reviewers can all sign off on. Delivered by one photographer out of Guatemala — not an agency rotation. Framework-friendly, grant-cycle-friendly, consent-first by default.",
        pains: [
            { i: "fa-solid fa-shield-halved", t: "Ethical compliance & safeguarding — every subject signs in their own language before publication." },
            { i: "fa-solid fa-hand-holding-heart", t: "Donor trust — provenance paperwork you can hand to a legal reviewer or an auditor." },
            { i: "fa-solid fa-plane", t: "Cheaper than flying your staff out — one on-ground photographer, no travel surcharges." },
            { i: "fa-solid fa-file-signature", t: "Fits framework agreements, grant cycles, Q1 reporting, and year-end fundraising calendars." },
        ],
        primary_cta: "Request a field shoot",
        secondary_cta: "See a sample package",
    },
    publisher: {
        eyebrow: "For editorial desks & publishers",
        headline_pre: "Truth on the",
        headline_accent: "frontlines.",
        description:
            "Verified field photography you can run without a 48-hour sourcing phone call. Every image ships with consent paperwork and C2PA metadata attached — so your fact-checkers, legal team, and photo editors have what they need in one file.",
        pains: [
            { i: "fa-solid fa-bolt", t: "Verification speed — provenance already baked into the file, not something to chase later." },
            { i: "fa-solid fa-scale-balanced", t: "No legal exposure on consent — signed releases attached to every subject." },
            { i: "fa-solid fa-fingerprint", t: "C2PA metadata out of camera — proves the file hasn't been altered." },
            { i: "fa-solid fa-pen-nib", t: "Caption integrity — AP-style, location-stamped, written by the photographer who was there." },
        ],
        primary_cta: "Browse the archive",
        secondary_cta: "Commission an assignment",
    },
};

export default function Home() {
    const nav = useNavigate();
    const [samplePkg, setSamplePkg] = useState(null);
    const [featured, setFeatured] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [inqSubmitting, setInqSubmitting] = useState(false);
    const [audience, setAudience] = useState("ngo");
    const [offerModal, setOfferModal] = useState(null);
    const copy = AUDIENCE_COPY[audience];
    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    const [form, setForm] = useState({
        organization_name: "", contact_name: "", contact_email: "",
        brief_description: "", location_region: "Western Highlands",
        deadline: "", budget_usd: "", usage_rights: "Exclusive 6-Month Editorial",
    });
    const [inqForm, setInqForm] = useState({ kind: "training", name: "", email: "", organization: "", message: "" });

    useEffect(() => {
        api.get("/sample-package").then((r) => setSamplePkg(r.data)).catch(() => {});
        api.get("/archive").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/shoot-requests", {
                ...form,
                budget_usd: form.budget_usd ? Number(form.budget_usd) : null,
            });
            toast.success("Request received. Trenton will respond within 24h.");
            setForm({ ...form, brief_description: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit — try again.");
        } finally { setSubmitting(false); }
    };

    const submitInquiry = async (e) => {
        e.preventDefault();
        setInqSubmitting(true);
        try {
            await api.post("/inquiries", inqForm);
            toast.success("Inquiry sent. Expect a reply within 2 business days.");
            setInqForm({ ...inqForm, message: "" });
        } catch (err) {
            toast.error("Could not submit — try again.");
        } finally { setInqSubmitting(false); }
    };

    return (
        <div className="min-h-screen" style={{ background: "var(--gt-bg)" }}>
            <Header />

            {/* HERO */}
            <section className="relative pt-28 pb-20 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none gt-grid-bg"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        <div className="lg:col-span-6 gt-fade-up" style={{ animationDelay: "60ms" }}>
                            {/* AUDIENCE SWITCHER */}
                            <div className="inline-flex items-center p-1 mb-6 gt-surface" data-testid="audience-switcher" style={{ gap: 4 }}>
                                <button
                                    data-testid="audience-ngo"
                                    onClick={() => setAudience("ngo")}
                                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
                                    style={{
                                        background: audience === "ngo" ? "var(--gt-accent)" : "transparent",
                                        color: audience === "ngo" ? "#1a0f0a" : "var(--gt-text-2)",
                                    }}
                                >
                                    I'm an NGO
                                </button>
                                <button
                                    data-testid="audience-publisher"
                                    onClick={() => setAudience("publisher")}
                                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
                                    style={{
                                        background: audience === "publisher" ? "var(--gt-accent)" : "transparent",
                                        color: audience === "publisher" ? "#1a0f0a" : "var(--gt-text-2)",
                                    }}
                                >
                                    I'm a Publisher
                                </button>
                            </div>
                            <div className="overline mb-6" data-testid="hero-eyebrow">{copy.eyebrow}</div>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" data-testid="hero-headline">
                                {copy.headline_pre}
                                <br />
                                <span style={{ color: "var(--gt-accent)" }}>{copy.headline_accent}</span>
                            </h1>
                            <p className="text-base lg:text-lg max-w-xl mb-6" style={{ color: "var(--gt-text-2)" }} data-testid="hero-description">
                                {copy.description}
                            </p>
                            <p className="text-sm max-w-xl mb-8 italic" style={{ color: "var(--gt-text)" }} data-testid="hero-shared-tagline">
                                Field imagery you can publish without second-guessing where it came from or who agreed to it.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <button data-testid="hero-primary-cta" onClick={() => scrollTo(audience === "ngo" ? "request-shoot" : "archive-preview")} className="gt-btn-primary px-6 py-3 text-sm">
                                    {copy.primary_cta} <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 11 }}></i>
                                </button>
                                <button data-testid="hero-secondary-cta" onClick={() => audience === "ngo" ? scrollTo("package") : scrollTo("request-shoot")} className="gt-btn-ghost px-6 py-3 text-sm">
                                    {copy.secondary_cta}
                                </button>
                                <a
                                    data-testid="hero-portfolio-cta"
                                    href="https://trentonfranklin.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="gt-btn-ghost px-6 py-3 text-sm inline-flex items-center"
                                >
                                    See my work <i className="fa-solid fa-arrow-up-right-from-square ml-2" style={{ fontSize: 10 }}></i>
                                </a>
                            </div>

                            {/* AUDIENCE PAIN POINTS */}
                            <div className="mt-8 space-y-2.5 max-w-xl" data-testid="audience-pains">
                                {copy.pains.map((p) => (
                                    <div key={p.t} className="flex items-start gap-3">
                                        <i className={p.i} style={{ color: "var(--gt-accent)", fontSize: 13, marginTop: 3 }}></i>
                                        <span className="text-sm" style={{ color: "var(--gt-text-2)" }}>{p.t}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md" data-testid="hero-quality-chips">
                                {[
                                    { i: "fa-solid fa-camera-retro", t: "Verified field photography" },
                                    { i: "fa-solid fa-signature", t: "Consent-documented" },
                                    { i: "fa-solid fa-file-contract", t: "Full licensing paperwork" },
                                ].map((s) => (
                                    <div key={s.t} className="gt-card p-4 flex items-start gap-3">
                                        <i className={s.i} style={{ color: "var(--gt-accent)", fontSize: 14, marginTop: 2 }}></i>
                                        <div className="text-xs leading-snug" style={{ color: "var(--gt-text)" }}>{s.t}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 overline" style={{ fontSize: 10 }} data-testid="hero-location-note">
                                Based in Guatemala · Covering Central America and beyond
                            </div>
                        </div>

                        <div className="lg:col-span-6 gt-fade-up" style={{ animationDelay: "180ms" }}>
                            <div className="relative gt-card overflow-hidden" data-testid="hero-photo-card">
                                <img
                                    alt="Cox's Bazar, Bangladesh, 2026 — Trenton Franklin / First Landing Studio"
                                    src="https://customer-assets-m6fa6gv7.emergentagent.net/wingman/d6e567c4-69cf-4455-87bc-9703d14c29bc/attachments/35a9627453e048d88b923b6553c53d49_IMG_4277.jpeg"
                                    className="w-full h-[480px] object-cover"
                                />
                                <div className="absolute top-4 left-4 flex flex-col gap-2">
                                    <span className="meta-chip"><span className="verif-dot"></span> C2PA Verified</span>
                                    <span className="meta-chip"><i className="fa-solid fa-signature" style={{ fontSize: 10 }}></i> Consent on file</span>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-5" style={{ background: "linear-gradient(180deg, transparent, rgba(10,11,14,0.95))" }}>
                                    <div className="overline mb-1">Cox's Bazar · Bangladesh</div>
                                    <div className="text-sm text-white leading-snug" data-testid="hero-photo-caption">
                                        Cox's Bazar, Bangladesh, 2026. Photo: Trenton Franklin / First Landing Studio.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section id="services" className="py-16 lg:py-24" style={{ borderTop: "1px solid var(--gt-border)" }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
                        <div className="lg:col-span-5">
                            <div className="overline mb-4">Four ways to work together</div>
                            <h2 className="font-display text-3xl lg:text-4xl leading-tight">
                                Named, scoped, and priced before we start.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                            <p className="text-base" style={{ color: "var(--gt-text-2)" }}>
                                No vague retainers, no open-ended day rates. Each offer below is a defined
                                deliverable with its own scope and paperwork, built for NGOs and publishers who
                                need predictability.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {SERVICES.map((s, i) => (
                            <div key={s.id} data-testid={`service-card-${s.id}`} className="gt-card p-6 flex flex-col gap-4 group" style={{ transition: "border-color 180ms ease" }}
                                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--gt-accent)")}
                                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--gt-border)")}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: "var(--gt-overlay)", border: "1px solid var(--gt-border)" }}>
                                        <i className={s.icon} style={{ color: "var(--gt-accent)", fontSize: 15 }}></i>
                                    </div>
                                    <div className="overline" style={{ fontSize: 10 }}>0{i + 1} / 04</div>
                                </div>
                                <div>
                                    <h3 className="font-display text-xl mb-2">{s.title}</h3>
                                    <p className="text-sm leading-relaxed" style={{ color: "var(--gt-text-2)" }}>{s.description}</p>
                                </div>
                                <div className="mt-auto pt-3 border-t space-y-3" style={{ borderColor: "var(--gt-border)" }}>
                                    <div>
                                        <div className="font-mono text-sm" style={{ color: "var(--gt-accent)" }}>{s.price_detail || s.price}</div>
                                        <div className="overline mt-1" style={{ fontSize: 10 }}>{s.target}</div>
                                    </div>
                                    {s.purchasable ? (
                                        <button
                                            data-testid={`service-pay-${s.id}`}
                                            onClick={() => setOfferModal(s)}
                                            className="gt-btn-primary w-full px-4 py-2.5 text-sm"
                                        >
                                            {s.id === "provenance_audit" ? "Order audit" : "Book a shoot"} <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 10 }}></i>
                                        </button>
                                    ) : (
                                        <button
                                            data-testid={`service-link-${s.id}`}
                                            onClick={() => s.link?.startsWith("#") ? scrollTo(s.link.slice(1)) : nav(s.link || "/archive")}
                                            className="gt-btn-ghost w-full px-4 py-2.5 text-sm"
                                        >
                                            {s.id === "ethical_archive_license" ? "Browse the archive" : "See subscriptions"} <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 10 }}></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* VERIFICATION & MONITORING — reframed for comms directors */}
                    <div className="mt-10 gt-card p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start" data-testid="verification-monitoring-card">
                        <div className="lg:col-span-2">
                            <div className="w-12 h-12 flex items-center justify-center" style={{ background: "var(--gt-accent)" }}>
                                <i className="fa-solid fa-location-dot" style={{ color: "#1a0f0a", fontSize: 18 }}></i>
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="overline mb-2">Add-on service</div>
                            <h3 className="font-display text-2xl mb-2">Verification & Monitoring</h3>
                            <p className="text-sm" style={{ color: "var(--gt-text-2)" }}>
                                Visual proof that your project exists and is working — geotagged, timestamped
                                field visits for community projects, supply chains, and development programs.
                                A photo your donors and compliance team can both trust.
                            </p>
                        </div>
                        <div className="lg:col-span-3 flex lg:justify-end items-end">
                            <button onClick={() => scrollTo("inquiry")} className="gt-btn-ghost px-5 py-3 text-sm" data-testid="verification-cta">
                                Ask about a visit <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 11 }}></i>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* SAMPLE PACKAGE */}
            <section id="package" className="py-16 lg:py-24" style={{ background: "var(--gt-bg-2)", borderTop: "1px solid var(--gt-border)", borderBottom: "1px solid var(--gt-border)" }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
                        <div className="lg:col-span-7">
                            <div className="overline mb-4">Sample story · Cox's Bazar, August 2026</div>
                            <h2 className="font-display text-3xl lg:text-4xl mb-3" data-testid="sample-package-title">
                                {samplePkg?.name || "Dawn comes quietly in Cox's Bazar"}
                            </h2>
                            <p className="max-w-2xl italic" style={{ color: "var(--gt-text)", fontFamily: "'Playfair Display', Georgia, serif" }}>
                                "Before the light fully breaks, the camps are already awake. You hear it first—soft
                                footsteps on packed earth, the creak of bamboo frames, a kettle set over a small flame.
                                Then the voices. Low, steady, familiar. Another day beginning in the largest refugee
                                settlement in the world."
                            </p>
                            <p className="mt-4 max-w-2xl text-sm" style={{ color: "var(--gt-text-2)" }}>
                                A short field assignment covering daily life in Cox's Bazar — morning routines,
                                shared water points, and the nearby fishing communities. An example of what a
                                Verified Field Shoot deliverable looks like end to end.
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 flex lg:justify-end items-end">
                            {samplePkg && (
                                <a
                                    data-testid="sample-package-view-btn"
                                    href={`/pkg/${samplePkg.download_token}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="gt-btn-ghost px-5 py-3 text-sm"
                                >
                                    View full package <i className="fa-solid fa-arrow-up-right-from-square ml-2" style={{ fontSize: 10 }}></i>
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {(samplePkg?.images || []).map((im, i) => (
                            <div key={im.id} className="gt-card overflow-hidden gt-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`sample-img-${i}`}>
                                <div className="relative">
                                    <img src={im.image_url} alt={im.title} className="w-full h-64 object-cover" />
                                    <span className="absolute top-3 left-3 meta-chip"><span className="verif-dot"></span> C2PA</span>
                                    <span className="absolute bottom-3 left-3 meta-chip" style={{ fontSize: 9, background: "rgba(10,11,14,0.85)" }}>Sample: Cox's Bazar, 2026</span>
                                </div>
                                <div className="p-5">
                                    <div className="overline mb-1" style={{ fontSize: 10 }}>{im.region} · {im.theme}</div>
                                    <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--gt-text)" }}>{im.caption}</p>
                                    <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gt-muted)" }}>
                                        {im.exif} · hash {im.c2pa_hash}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8 gt-surface p-5 flex items-start gap-3">
                        <i className="fa-solid fa-signature" style={{ color: "var(--gt-accent)", fontSize: 16, marginTop: 3 }}></i>
                        <div>
                            <div className="overline mb-1">Consent note</div>
                            <p className="text-sm" style={{ color: "var(--gt-text-2)" }}>
                                All subjects photographed with informed consent. Documentation on file. Usage rights available on request.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ARCHIVE PREVIEW */}
            <section id="archive-preview" className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                        <div>
                            <div className="overline mb-4">The archive</div>
                            <h2 className="font-display text-3xl lg:text-4xl">License a photo. Keep the paperwork.</h2>
                        </div>
                        <button data-testid="archive-open-btn" onClick={() => nav("/archive")} className="gt-btn-primary px-5 py-3 text-sm w-max">
                            Open the Archive <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 11 }}></i>
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {featured.map((im) => (
                            <button
                                key={im.id}
                                data-testid={`archive-preview-${im.id}`}
                                onClick={() => nav(`/archive/${im.id}`)}
                                className="gt-card overflow-hidden text-left group"
                            >
                                <div className="relative h-56 overflow-hidden">
                                    <img src={im.image_url} alt={im.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute top-3 right-3 flex gap-1 flex-wrap justify-end">
                                        <span className="meta-chip" style={{ fontSize: 9 }}>{im.theme}</span>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="font-display text-base mb-1">{im.title}</div>
                                    <div className="overline" style={{ fontSize: 10 }}>{im.region} · {im.community || "—"}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* REQUEST SHOOT FORM */}
            <section id="request-shoot" className="py-16 lg:py-24" style={{ background: "var(--gt-bg-2)", borderTop: "1px solid var(--gt-border)", borderBottom: "1px solid var(--gt-border)" }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="overline mb-4">Request a field shoot</div>
                        <h2 className="font-display text-3xl lg:text-4xl mb-4">Send a brief. Get a quote in 24 hours.</h2>
                        <p style={{ color: "var(--gt-text-2)" }}>
                            Tell us what you need and where. You'll get back a fixed-scope quote with the
                            number of images, timeline, usage rights, and consent plan — all before anything
                            is booked.
                        </p>
                        <div className="mt-8 space-y-3">
                            {[
                                { i: "fa-solid fa-handshake", t: "You talk to the photographer directly — no agency layers." },
                                { i: "fa-solid fa-shield-halved", t: "Every subject signs a consent release before publication." },
                                { i: "fa-solid fa-fingerprint", t: "Every file ships with C2PA metadata that proves it hasn't been altered." },
                            ].map((row) => (
                                <div key={row.t} className="flex items-start gap-3">
                                    <i className={row.i} style={{ color: "var(--gt-accent)", fontSize: 14, marginTop: 3 }}></i>
                                    <span className="text-sm" style={{ color: "var(--gt-text-2)" }}>{row.t}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <form onSubmit={submit} className="lg:col-span-7 gt-card p-6 lg:p-8 space-y-4" data-testid="shoot-request-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="overline block mb-2">Organization / Publication</label>
                                <input data-testid="shoot-org-input" required className="gt-input" value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} placeholder="Geneva Impact Desk" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Contact Name</label>
                                <input data-testid="shoot-name-input" className="gt-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Full name" />
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-2">Contact Email</label>
                            <input data-testid="shoot-email-input" required type="email" className="gt-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="you@organization.org" />
                        </div>
                        <div>
                            <label className="overline block mb-2">Assignment Brief & Objectives</label>
                            <textarea data-testid="shoot-brief-input" required className="gt-input" value={form.brief_description} onChange={(e) => setForm({ ...form, brief_description: e.target.value })} placeholder="Scope, subject matter, key story angles, deliverables needed…" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="overline block mb-2">Field Location</label>
                                <select data-testid="shoot-location-input" className="gt-input" value={form.location_region} onChange={(e) => setForm({ ...form, location_region: e.target.value })}>
                                    {["Guatemala City & Central", "Western Highlands", "Petén & Northern Jungle", "Pacific Coast", "Custom Central America Spot"].map((o) => <option key={o}>{o}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="overline block mb-2">Target Deadline</label>
                                <input data-testid="shoot-deadline-input" type="date" className="gt-input" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                            </div>
                            <div>
                                <label className="overline block mb-2">Budget (USD)</label>
                                <input data-testid="shoot-budget-input" type="number" min="0" className="gt-input" value={form.budget_usd} onChange={(e) => setForm({ ...form, budget_usd: e.target.value })} placeholder="2500" />
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-2">Usage Rights</label>
                            <select data-testid="shoot-rights-input" className="gt-input" value={form.usage_rights} onChange={(e) => setForm({ ...form, usage_rights: e.target.value })}>
                                {["Exclusive 6-Month Editorial", "Non-Exclusive Global NGO", "Full Ownership / Raw Transfer"].map((o) => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <div className="overline" style={{ fontSize: 10 }}>Reply within 24h · USD invoicing · pay by card</div>
                            <button data-testid="shoot-submit-btn" disabled={submitting} className="gt-btn-primary px-6 py-3 text-sm">
                                {submitting ? "Sending…" : "Send brief"}
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            {/* TRUST / C2PA */}
            <section id="trust" className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-5">
                            <div className="overline mb-4">What C2PA means</div>
                            <h2 className="font-display text-3xl lg:text-4xl mb-4">You can prove where every photo came from.</h2>
                            <p style={{ color: "var(--gt-text-2)" }} className="mb-4">
                                Every image we deliver carries <strong style={{ color: "var(--gt-text)" }}>Content Provenance and Authenticity (C2PA)</strong> metadata —
                                an open standard that records where and when a photo was taken and verifies it has
                                not been altered.
                            </p>
                            <p style={{ color: "var(--gt-text-2)" }} className="mb-6 text-sm">
                                In plain terms: your legal team, fact-checkers, donors, and auditors can all
                                verify a photo themselves. No phone calls to the photographer. No sourcing memos.
                                No doubt.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[
                                { i: "fa-solid fa-fingerprint", t: "C2PA metadata", d: "Signed at the camera. Any edit after that is visible — so you know the file hasn't been quietly changed." },
                                { i: "fa-solid fa-signature", t: "Documented consent", d: "Every featured subject reviews and signs a release in their own language before publication." },
                                { i: "fa-solid fa-location-dot", t: "Location & time", d: "Exact GPS, timestamp, and field notes travel with the file — not in a separate email." },
                                { i: "fa-solid fa-ban", t: "No AI-generated images", d: "First Landing Studio never produces synthetic or AI-generated images. Real photographs only." },
                            ].map((f) => (
                                <div key={f.t} className="gt-card p-5">
                                    <i className={f.i} style={{ color: "var(--gt-accent)", fontSize: 16 }}></i>
                                    <div className="font-display text-lg mt-3 mb-1">{f.t}</div>
                                    <p className="text-sm" style={{ color: "var(--gt-text-2)" }}>{f.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SUBSCRIPTION TIERS - self-serve */}
            <section id="subscribe" className="py-16 lg:py-24" style={{ borderTop: "1px solid var(--gt-border)" }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
                        <div className="lg:col-span-6">
                            <div className="overline mb-4">Monthly subscriptions</div>
                            <h2 className="font-display text-3xl lg:text-4xl">New field photos in your inbox every month.</h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                            <p className="text-base" style={{ color: "var(--gt-text-2)" }}>
                                Pay the first month by card. Every tier includes signed consent releases, written
                                captions, and C2PA metadata on every file. Cancel anytime by email.
                            </p>
                        </div>
                    </div>
                    <SubscriptionTiers />
                </div>
            </section>

            {/* INQUIRY: TRAINING + GENERAL */}
            <section id="inquiry" className="py-16 lg:py-24" style={{ background: "var(--gt-bg-2)", borderTop: "1px solid var(--gt-border)" }}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="overline mb-3">Audit · Monitoring · General</div>
                        <h2 className="font-display text-3xl lg:text-4xl">Not ready to commission a shoot? Just say hello.</h2>
                    </div>
                    <form onSubmit={submitInquiry} className="gt-card p-6 lg:p-8 space-y-4" data-testid="inquiry-form">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="overline block mb-2">Inquiry Type</label>
                                <select data-testid="inq-kind" className="gt-input" value={inqForm.kind} onChange={(e) => setInqForm({ ...inqForm, kind: e.target.value })}>
                                    <option value="verification">Verification & Monitoring visit</option>
                                    <option value="training">Provenance Audit</option>
                                    <option value="general">General inquiry</option>
                                </select>
                            </div>
                            <div>
                                <label className="overline block mb-2">Name</label>
                                <input required data-testid="inq-name" className="gt-input" value={inqForm.name} onChange={(e) => setInqForm({ ...inqForm, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="overline block mb-2">Email</label>
                                <input required type="email" data-testid="inq-email" className="gt-input" value={inqForm.email} onChange={(e) => setInqForm({ ...inqForm, email: e.target.value })} />
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-2">Organization</label>
                            <input data-testid="inq-org" className="gt-input" value={inqForm.organization} onChange={(e) => setInqForm({ ...inqForm, organization: e.target.value })} />
                        </div>
                        <div>
                            <label className="overline block mb-2">Message</label>
                            <textarea required data-testid="inq-message" className="gt-input" value={inqForm.message} onChange={(e) => setInqForm({ ...inqForm, message: e.target.value })} />
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="overline" style={{ fontSize: 10 }}>Goes to contact@trentonfranklin.com</div>
                            <button data-testid="inq-submit" disabled={inqSubmitting} className="gt-btn-primary px-6 py-3 text-sm">
                                {inqSubmitting ? "Sending…" : "Send message"}
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            <Footer />
            {offerModal && <OfferCheckoutModal offer={offerModal} onClose={() => setOfferModal(null)} />}
        </div>
    );
}

function OfferCheckoutModal({ offer, onClose }) {
    const [form, setForm] = useState({ email: "", organization: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/offers/checkout", {
                slug: offer.slug, email: form.email, organization: form.organization,
                notes: form.notes, origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open checkout.");
            setSubmitting(false);
        }
    };
    const amountLabel = offer.id === "provenance_audit" ? "$450 flat" : "$1,500 deposit";
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(10,11,14,0.75)", backdropFilter: "blur(6px)" }} data-testid="offer-modal">
            <form onSubmit={submit} className="gt-card p-6 lg:p-8 w-full max-w-md space-y-4">
                <div className="overline">{offer.title} · {amountLabel}</div>
                <h3 className="font-display text-2xl">{offer.id === "provenance_audit" ? "Order a Provenance Audit" : "Book a Verified Field Shoot"}</h3>
                <p className="text-sm" style={{ color: "var(--gt-text-2)" }}>{offer.description}</p>
                <input required type="email" placeholder="Billing email" className="gt-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="offer-email" />
                <input required placeholder="Organization" className="gt-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="offer-org" />
                <textarea placeholder={offer.id === "provenance_audit" ? "Library size, hosting location, any specifics…" : "Target region, dates, subject matter…"} className="gt-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="offer-notes" />
                <div className="flex items-center justify-between gap-2 pt-2">
                    <button type="button" onClick={onClose} className="gt-btn-ghost px-4 py-2 text-sm" data-testid="offer-cancel">Cancel</button>
                    <button disabled={submitting} className="gt-btn-primary px-5 py-2.5 text-sm" data-testid="offer-continue">
                        {submitting ? "Opening Stripe…" : "Continue to payment"}
                    </button>
                </div>
                <div className="overline text-center" style={{ fontSize: 10 }}>Secure payment by card via Stripe · USD</div>
            </form>
        </div>
    );
}

function SubscriptionTiers() {
    const [tiers, setTiers] = useState([]);
    const [picking, setPicking] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ email: "", organization: "" });

    useEffect(() => { api.get("/subscriptions/tiers").then((r) => setTiers(r.data)).catch(() => {}); }, []);

    const start = (tier) => { setSelected(tier); setShowForm(true); };

    const submit = async (e) => {
        e.preventDefault();
        setPicking(selected.lookup_key);
        try {
            const { data } = await api.post("/subscriptions/checkout", {
                lookup_key: selected.lookup_key, email: form.email, organization: form.organization,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setPicking(null); }
    };

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {tiers.map((t, i) => (
                    <div key={t.lookup_key} className="gt-card p-6 flex flex-col gap-4" data-testid={`sub-tier-${t.lookup_key}`}
                         style={{ borderColor: i === 1 ? "var(--gt-accent)" : "var(--gt-border)" }}>
                        {i === 1 && <div className="overline" style={{ color: "var(--gt-accent)", fontSize: 10 }}>Most popular</div>}
                        <div>
                            <div className="font-display text-2xl">{t.name}</div>
                            <p className="text-sm mt-2" style={{ color: "var(--gt-text-2)" }}>{t.subtitle}</p>
                        </div>
                        <div className="pt-3 border-t" style={{ borderColor: "var(--gt-border)" }}>
                            <div className="font-display text-4xl" style={{ color: "var(--gt-accent)" }}>${(t.amount / 100).toLocaleString()}<span className="text-base font-body" style={{ color: "var(--gt-text-2)" }}>/mo</span></div>
                        </div>
                        <ul className="space-y-2 text-sm" style={{ color: "var(--gt-text-2)" }}>
                            {t.features.map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <i className="fa-solid fa-check" style={{ color: "var(--gt-accent)", fontSize: 11, marginTop: 4 }}></i> {f}
                                </li>
                            ))}
                        </ul>
                        <button onClick={() => start(t)} disabled={picking === t.lookup_key} className="gt-btn-primary px-5 py-3 text-sm mt-auto" data-testid={`sub-start-${t.lookup_key}`}>
                            {picking === t.lookup_key ? "Opening Stripe…" : "Subscribe"} <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 11 }}></i>
                        </button>
                    </div>
                ))}
            </div>
            {showForm && selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(10,11,14,0.75)", backdropFilter: "blur(6px)" }} data-testid="sub-modal">
                    <form onSubmit={submit} className="gt-card p-6 lg:p-8 w-full max-w-md space-y-4">
                        <div className="overline">{selected.name} · ${(selected.amount / 100).toLocaleString()}/mo</div>
                        <h3 className="font-display text-2xl">Start your subscription</h3>
                        <input required type="email" placeholder="Billing email" className="gt-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sub-email" />
                        <input required placeholder="Organization" className="gt-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="sub-org" />
                        <div className="flex items-center justify-between gap-2 pt-2">
                            <button type="button" onClick={() => { setShowForm(false); setSelected(null); }} className="gt-btn-ghost px-4 py-2 text-sm">Cancel</button>
                            <button disabled={picking === selected.lookup_key} className="gt-btn-primary px-5 py-2.5 text-sm" data-testid="sub-continue">
                                {picking === selected.lookup_key ? "Opening Stripe…" : "Continue to Stripe"}
                            </button>
                        </div>
                        <div className="overline text-center" style={{ fontSize: 10 }}>First month billed in USD · cancel by email anytime</div>
                    </form>
                </div>
            )}
        </div>
    );
}
